'use client'

import { useTranslations } from 'next-intl'
import { CSSProperties } from 'react'

import toastStore from '../../stores/toast-store'
import { GetValueByPath } from '../../utils'

import { useTableContext, useVisibleColumns } from '../context/table-context'
import { Sorter, TableColumn, TFilter } from '../types'
import { TFilterCollection } from '../types/table-props'

// ---- filters ---------------------------------------------------------------------------------

/** A filter is applied (and sent to getData) only once it has a column, an operator and a value. */
export const isFilterComplete = (filter: Partial<TFilter>): filter is TFilter => {
    const { value } = filter
    if (!filter.column || !filter.operator)
        return false

    if (value === undefined || value === null || value === '')
        return false

    if (typeof value === 'number' && Number.isNaN(value))
        return false

    return !(Array.isArray(value) && value.length === 0)
}

export const getCompleteFilters = (filters: TFilterCollection) =>
    Object.values(filters).filter(isFilterComplete)

/** Stable key of the applied filters: it changes only when the query sent to getData would change. */
export const serializeAppliedFilters = (filters: TFilterCollection) =>
    Object.entries(filters)
        .filter(([, filter]) => isFilterComplete(filter))
        .map(([key, filter]) => `${key}_${JSON.stringify(filter)}`)
        .sort()
        .join('|')

// ---- client-side sorting (static `data`) -----------------------------------------------------

const compareValues = (a: unknown, b: unknown, type?: TableColumn<unknown>['type']) => {
    if (type === 'number' || (typeof a === 'number' && typeof b === 'number'))
        return Number(a) - Number(b)

    if (type === 'date' || (a instanceof Date && b instanceof Date))
        return new Date(a as string).getTime() - new Date(b as string).getTime()

    if (type === 'boolean' || (typeof a === 'boolean' && typeof b === 'boolean'))
        return Number(a) - Number(b)

    return String(a).localeCompare(String(b), undefined, { numeric: true })
}

export const sortRows = <T,>(rows: T[], sorter: Sorter | null, columns: TableColumn<T>[]) => {
    if (!sorter)
        return rows

    const column = columns.find(c => c.accessor === sorter.column)
    const direction = sorter.order === 'asc' ? 1 : -1
    const getValue = (row: T) => GetValueByPath(row, sorter.column as TableColumn<T>['accessor']) as unknown

    return [...rows].sort((a, b) => {
        const valueA = getValue(a)
        const valueB = getValue(b)

        // empty values always last
        const emptyA = valueA === undefined || valueA === null || valueA === ''
        const emptyB = valueB === undefined || valueB === null || valueB === ''
        if (emptyA || emptyB)
            return emptyA === emptyB ? 0 : emptyA ? 1 : -1

        return compareValues(valueA, valueB, column?.type) * direction
    })
}

// ---- drag & drop ----------------------------------------------------------------------------

type TOrdered = { order_index?: unknown }

/**
 * Moves a row and renumbers `order_index` (when the rows have one) with the same set of values,
 * so the numbering keeps its base (0- or 1-based).
 */
export const reorderRows = <T,>(rows: T[], from: number, to: number): T[] => {
    const reordered = [...rows]
    const [moved] = reordered.splice(from, 1)
    reordered.splice(to, 0, moved)

    const orderIndexes = rows
        .map(row => (row as TOrdered).order_index)
        .filter((value): value is number => typeof value === 'number')
    if (orderIndexes.length !== rows.length)
        return reordered

    orderIndexes.sort((a, b) => a - b)
    return reordered.map((row, index) => ({ ...row, order_index: orderIndexes[index] }))
}

// ---- layout ---------------------------------------------------------------------------------

export type TLeadingCell = 'drag' | 'select' | 'expand'

/** px width of the drag / select / expand cells when they have to be sticky */
export const LEADING_CELL_WIDTH = 40

/** Cells rendered before the data columns, in this order, in every row (header, body, summary...). */
export const getLeadingCells = ({
    draggable,
    selectable,
    expandable,
}: {
    draggable?: boolean
    selectable?: boolean
    expandable?: unknown
}) => {
    const cells: TLeadingCell[] = []
    if (draggable)
        cells.push('drag')
    if (selectable)
        cells.push('select')
    if (expandable)
        cells.push('expand')

    return cells
}

/** `left` offsets of fixed columns: leading cells plus the widths of the *previous* fixed columns. */
export const getStickyOffsets = <T,>(columns: TableColumn<T>[], leadingWidth: number) => {
    let offset = leadingWidth
    return columns.map(column => {
        if (column.fixed !== 'left')
            return undefined

        const left = offset
        offset += column.width ?? 0
        return left
    })
}

const stickyStyle = (left: number, width: number | undefined): CSSProperties => ({
    position: 'sticky',
    left: left + 'px',
    ...(width !== undefined && {
        width: width + 'px',
        minWidth: width + 'px',
        maxWidth: width + 'px',
    }),
    zIndex: 2,
})

export const useTableLayout = <T,>() => {
    const { draggable, selectable, expandable, actions } = useTableContext<T>()
    const columns = useVisibleColumns<T>()

    const leadingCells = getLeadingCells({ draggable, selectable, expandable })
    const hasFixedColumns = columns.some(column => column.fixed === 'left')
    const stickyOffsets = getStickyOffsets(
        columns,
        hasFixedColumns ? leadingCells.length * LEADING_CELL_WIDTH : 0
    )
    const hasActionsColumn = actions.length > 0

    return {
        columns,
        leadingCells,
        hasActionsColumn,
        /** number of cells of a full row */
        colSpan: leadingCells.length + columns.length + (hasActionsColumn ? 1 : 0),
        /** leading cells stay in place together with fixed columns */
        leadingCellStyle: (index: number): CSSProperties | undefined =>
            hasFixedColumns ? stickyStyle(index * LEADING_CELL_WIDTH, LEADING_CELL_WIDTH) : undefined,
        columnStyle: (index: number): CSSProperties | undefined => {
            const left = stickyOffsets[index]
            return left === undefined ? undefined : stickyStyle(left, columns[index].width)
        },
    }
}

// ---- errors ---------------------------------------------------------------------------------

/** Error → toast, the way all table actions report failures. */
export const useActionErrorHandler = () => {
    const t = useTranslations('table')
    const tGeneric = useTranslations()

    return (error: unknown, title: string = t('error-occured')) => {
        const rawMessage = (error as { message?: unknown } | null)?.message
        const message = typeof rawMessage === 'string' ? rawMessage : String(error)

        if (message.startsWith('error.')) {
            toastStore.updateToast({ title, message: tGeneric(message as any), type: 'error' })
            return
        }

        console.error(error)
        toastStore.updateToast({ title, message, type: 'error' })
    }
}
